import { uniqBy, pickBy, has } from 'lodash';
import Backbone from 'backbone-model';

import Entity from 'lib/Entity';
import Group from 'entities/Group';
import LinkPreview from 'entities/LinkPreview';
import MessageRecipients from 'collections/MessageRecipients';
import MessageSender from 'entities/MessageSender';
import ReactionSummary from 'entities/ReactionSummary';
import uuid from 'uuid/v4';
// @ts-expect-error ts-migrate(2614) FIXME: Module '"reducers/roleBasedMessagingSearch"' has n... Remove this comment to see the full error message
import { MessageFilterType } from 'reducers/roleBasedMessagingSearch';
import { Backbone$Id } from 'types/typescript/Backbone';
import { V2File } from 'clients/api/fileUploads';

export const TARGET_TYPES = {
  STUDENT_ID_LIST_TARGET: 'student_id_list_target',
  ORGANIZATION_TARGET: 'district_target',
  GROUP_TARGET: 'group_target',
  GROUP_CHAT_TARGET: 'group_chat_target',
  INDIVIDUAL_TARGET: 'individual_target',
} as const;

export type OrganizationTarget = {
  type: 'district_target';
  org_id?: Backbone$Id;
  name?: string;
  uuid: string;
  selectedFilters: MessageFilterType[];
};

export type GroupTargetRole = 'everyone' | 'student' | 'teacher' | 'parent' | 'admin';

export function isGroupTargetRole(role?: string): role is GroupTargetRole {
  if (!role) {
    return false;
  }
  return ['everyone', 'student', 'teacher', 'parent', 'admin'].includes(role);
}

export type GroupTarget = {
  type: 'group_target';
  role: GroupTargetRole;
  group_id?: Backbone$Id;
  name?: string;
  uuid: string;
  preview?: {
    preview: string;
    sent_at: any;
  };
  count?: number;
  selectedFilters: MessageFilterType[];
};

export type GroupChatTarget = {
  type: 'group_chat_target';
  chat_id: Backbone$Id;
  uuid: string;
};

export type IndividualTarget = {
  type: 'individual_target';
  member_id: Backbone$Id;
  uuid: string;
};

export type ListTarget = {
  type: 'student_id_list_target';
  uuid: string;
  org_uuid: string;
};

export type Target =
  | OrganizationTarget
  | GroupTarget
  | GroupChatTarget
  | IndividualTarget
  | ListTarget;

/**
 * Announcement, really. (This was named before we had chat.)
 */
export class Message extends Entity {
  // @ts-expect-error ts-migrate(2425) FIXME: Class 'Entity' defines instance member property 'u... Remove this comment to see the full error message
  urlRoot() {
    return '/messages';
  }

  defaults() {
    return {
      attachmentId: null,
      body: '',
      created_at: new Date(Date.now()),
      files: [],
      group_ids: null,
      params: {},
      payment_req: null,
      received_count: null,
      recipients: [],
      send_at: null,
      sent_at: null,
      status: null,
      uuid: uuid(),
      is_client_created: false,
      body_attributes: [],
      link_previews: [],
      translation_details: null,
    };
  }

  parse(response: any) {
    const data = Object.assign({}, response);

    if (data['created_at']) {
      data['created_at'] = new Date(data['created_at']);
    }

    if (data['send_at']) {
      data['send_at'] = new Date(data['send_at']);
    }

    if (data['sent_at']) {
      data['sent_at'] = new Date(data['sent_at']);
    }

    if (data['sender']) {
      data['sender'] = new MessageSender(data['sender']);
    }

    if (data['recipients']) {
      data['recipients'] = MessageRecipients.fromJSON(data['recipients']);
    }

    if (data['reaction_summary']) {
      data['reaction_summary'] = ReactionSummary.fromJSON(data['reaction_summary']);
    }

    if (data.linkPreviews) {
      data['link_previews'] = data.linkPreviews;
    }

    if (data['link_previews']) {
      data['link_previews'] = data['link_previews'].map((linkPreview: any) =>
        LinkPreview.fromJSON(linkPreview)
      );
    }

    if (response.createdAt) {
      data['created_at'] = new Date(response.createdAt);
    }

    return data;
  }

  getLinkPreviews(): [LinkPreview] {
    return this.get('link_previews');
  }

  getReactionSummary(): ReactionSummary {
    return this.get('reaction_summary') || new ReactionSummary();
  }

  canReact(): boolean {
    return !!this.get('can_react');
  }

  getSeq(): number | undefined {
    return this.get('seq');
  }

  getType(): string {
    return this.get('type');
  }

  getAttachmentId() {
    return this.get('attachmentId');
  }

  getISO639Locale(): string | undefined {
    let locale = this.get('locale');

    if (locale) {
      [locale] = locale.split('-');
    }

    return locale;
  }

  getBody(): string | undefined {
    return this.get('body');
  }

  getSender(): MessageSender | undefined {
    const sender = this.get('sender');
    // the reason we need to check both the signature and the name here
    // is because the GQL mutation that loads a scheduled message into the
    // Apollo cache returns both a `name` and a `signature` field,
    // while the v2 endpoint that loads scheduled messages into Flux only
    // returns a misnamed `name` field (which ACTUALLY returns a signature).
    const signatureField = sender && sender.get('signature');
    if (signatureField) {
      sender.set('name', signatureField);
    }
    return sender;
  }

  getCreatedAt() {
    return this.get('created_at');
  }

  getSendAt() {
    return this.get('send_at');
  }

  getSentTo() {
    return this.get('sent_to');
  }

  getSentAt() {
    const sentAt = this.get('sent_at') || this.get('created_at');
    return this.getStatus() === 'pending' ? this.getSendAt() : sentAt;
  }

  getStatus() {
    return this.get('status');
  }

  setStatus(status: any): Backbone.Model {
    return this.set({ status });
  }

  getRecipients(): any[] {
    return this.get('recipients');
  }

  getGroupIds() {
    return this.get('group_ids') || this.getGroupIdsFromRecipients();
  }

  getGroupIdsFromRecipients() {
    return this.getRecipients()
      .filter((recipient) => recipient.isGroup())
      .map((recipient) => recipient.id);
  }

  getReceivedCount() {
    return this.get('received_count');
  }

  getFiles(): V2File[] {
    return this.get('files');
  }

  // The casing in this getter looks like an error, but it's actually because
  // translations is a more recent (2020 onwards) feature that only uses
  // graphql in its implementation. To integrate with the visual composer it
  // needs to integrated with the message entity. To do that it needs to refer
  // to a camelcase field, even though most of the other fields are
  // snake_cased (due to the conventions of the old v2 api endpoints).
  getTranslationDetails() {
    return this.get('translationDetails');
  }

  getFile(): V2File | null {
    const files = this.getFiles();
    return files.length > 0 ? files[0] : null;
  }

  getGapParams() {
    return this.get('params');
  }

  getBodyAttributes(): BodyAttribute[] {
    return this.get('body_attributes');
  }

  getPreview() {
    const preview = this.getBody() || (this.getFile() && '1 Attachment 📚') || '<error>';
    return `Announcement: ${preview}`;
  }
  hasBody(): boolean {
    const body = this.getBody();
    if (body == null) {
      return false;
    }
    return body.trim().length > 0;
  }
  // $FlowFixMe target types messed up
  getThreads(): Target[] {
    let result = [];
    const threads: any[] = this.get('threads');
    if (threads) {
      result = threads.map((t) => {
        const type = has(t, 'group_id')
          ? TARGET_TYPES.GROUP_TARGET
          : TARGET_TYPES.INDIVIDUAL_TARGET;
        return {
          type,
          ...t,
        };
      });
    }
    return result;
  }
  /**
   * Returns a duplicate free array of group threads.
   */ getGroupThreads(): GroupTarget[] {
    const groupThreads = this.getThreads().reduce<any[]>((arr, current) => {
      if (current.type === TARGET_TYPES.GROUP_TARGET) {
        arr.push(current);
      }
      return arr;
    }, []);

    return uniqBy(groupThreads, (thread) => thread.group_id);
  }
  /**
   * Returns a duplicate free array of subcriber (individual target) threads.
   */ getSubscriberThreads(): IndividualTarget[] {
    const subscriberThreads = this.getThreads().reduce<any[]>((arr, current) => {
      if (current.type === TARGET_TYPES.INDIVIDUAL_TARGET) {
        arr.push(current);
      }
      return arr;
    }, []);
    return uniqBy(subscriberThreads, (thread) => thread.member_id);
  }
  getUuid() {
    return this.get('uuid');
  }
  getReadSummary() {
    return this.get('read_summary');
  }
  toGraphQL() {
    const messageUuid = this.get('uuid');
    const sender = this.getSender();
    const senderJSON = sender ? sender.toJSON() : {};

    const files = [];
    const file = this.getFile();

    if (file) {
      files.push({
        uuid: file.id,
        size: file.size,
        name: file.name,
        url: file.urls.cdn,
        contentType: file.content_type,
        previewWidth: file.preview_width || 0,
        previewHeight: file.preview_height || 0,
        __typename: 'File',
      });
    }
    let groupSummary = null;

    if (this.getGroupSummary()) {
      groupSummary = {
        __typename: 'Class',
        ...this.getGroupSummary().toJSON(),
      };
    }
    return {
      key: `${messageUuid}.${this.getSeq() || 0}`,
      seq: (this.getSeq() || 0).toString(),
      item: {
        uuid: messageUuid,
        type: 'text',
        canReact: true,
        body: this.getBody(),
        createdAt: new Date(),
        sentAt: this.getSentAt(),
        sendAt: this.getSendAt(),
        status: this.getStatus(),
        preview: this.getPreview(),
        threads: this.getThreads(),
        linkPreviews: this.getLinkPreviews(),
        bodyAttributes: this.getBodyAttributes(),
        publicUrl: null,
        reactionSummary: null,
        chat: {
          uuid: messageUuid,
          __typename: 'ChatStream',
        },
        sender: {
          id: senderJSON.id,
          uuid: senderJSON.uuid,
          name: senderJSON.display_name || senderJSON.name,
          initials: senderJSON.initials,
          avatarUrl: senderJSON.avatarUrl,
          contactabilityStatus: senderJSON.contactabilityStatus,
          reachabilityInfo: senderJSON.reachabilityInfo,
          __typename: 'RelatedUser',
        },
        target: null,
        files,
        group: groupSummary,
        locale: this.getISO639Locale(),
        readSummary: { state: 'delivered', __typename: 'MessageReadSummary' },
        urgent: this.get('urgent') || false,
        __typename: 'MessageItem',
      },
      __typename: 'SequenceItem',
    };
  }
  getTargets(): Target[] {
    let result: Target[] = [];
    const targets: Target[] = this.get('targets');
    if (targets) {
      result = targets.map((t) => {
        const type = has(t, 'group_id')
          ? TARGET_TYPES.GROUP_TARGET
          : TARGET_TYPES.INDIVIDUAL_TARGET;
        return {
          // @ts-expect-error ts-migrate(2783) FIXME: 'type' is specified more than once, so this usage ... Remove this comment to see the full error message
          type,
          ...t,
        };
      });
    }
    return result;
  }
  getRoles() {
    const targets = this.getTargets()
      .filter((t) => 'role' in t)
      .map((t) => (t as GroupTarget).role);
    return targets.length > 0
      ? targets
      : this.getThreads()
          .filter((t) => 'role' in t)
          .map((t) => (t as GroupTarget).role);
  }
  getIsClientCreated(): boolean {
    return this.get('is_client_created');
  }
  getGroupSummary() {
    return this.get('group_summary');
  }
  sentByCurrentUser() {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const sender = this.getSender();
    return sender && sender.getUuid() === CurrentUser.getUserUuid();
  } // Used when adding file by file ID
  setOnlyFile(file: Object) {
    return this.set('files', [file]);
  }
  send(): Promise<Message | void> {
    if (!this.isNew()) {
      // The message has already been sent.
      return new Promise((resolve) => resolve(this));
    }
    const targets = this.getTargets();
    const message = {
      body: this.getBody(),
      group_ids: targets.length ? null : this.getGroupIds(),
      send_at: this.getSendAt(),
      files: this.getFiles(),
      uuid: this.getUuid(),
      payment_req_uuid: this.get('payment_req_uuid'),
      is_client_created: true,
      targets,
    }; // remove any undefined or null options
    const cleanMessage = pickBy(
      message,
      (v: any): boolean => typeof v !== 'undefined' && v !== null
    );
    return this.save(
      {
        sent_at: new Date(),
      },
      {
        data: {
          cleanMessage,
        },
      }
    );
  }
  static createMessage(
    groups: Group[] | undefined,
    text: string,
    sendAt: Date | string | undefined,
    files: V2File[],
    sender: string,
    sentAt: Date | string | undefined,
    targets: Target[],
    newUuid: string | undefined
  ): Message {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const user = CurrentUser.isSignedIn() ? CurrentUser.getUser() : {};
    const options = {
      group_ids: groups ? groups.map((group) => group.id) : null,
      recipients: groups
        ? groups.map((group) => ({
            id: group.id,
            name: group.getName(),
            type: 'group',
          }))
        : null,
      send_at: sendAt,
      sent_at: sentAt,
      body: text,
      sender: sender || {
        id: user.id,
        name: user.getSignature(),
      },
      files,
      targets,
      uuid: newUuid,
    }; // remove any undefined or null options
    const cleanOptions = pickBy(
      options,
      (v: any): boolean => typeof v !== 'undefined' && v !== null
    );
    return Message.fromJSON(cleanOptions);
  }
  /**
   * @return {Message} a version of this message suitable to send again
   */ reuse() {
    const sendable = this.clone();
    sendable.set({
      status: null,
      send_at: null,
      sent_at: null,
      uuid: uuid(),
    });
    return sendable;
  }
  // This should not update 'files' because the urls // aren't ready until returned by the server in patch
  update(body: string, targets: Target[], sendAt: string) {
    this.set({
      body,
      targets,
      send_at: sendAt,
    });
    const changes = this.changedAttributes() || {}; // The message was changed to unscheduled. // Placed below changes because it's only // meant for view purposes
    if (!sendAt) {
      this.set({
        status: null,
        sent_at: new Date(),
      });
    }
    return changes;
  }
  patch(changes: any, files: any) {
    const message = Object.assign(changes, { files });
    return this.save({ message }, { patch: true });
  }
  toJSON(): Object {
    return this.recursivelyToJSON();
  }
}
export class GapMessage extends Message {
  getType() {
    return 'gap';
  }
  getUuid() {
    return 'gap-uuid';
  }
}
export default Message;
